import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { translations } from '../translations';
import LoginModal from './LoginModal';
import logo from '../assets/images/logo.png';
import '../styles/components/Navbar.css';

function Navbar({ language, setLanguage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const t = translations[language];
  const location = useLocation();
  const navigate = useNavigate();

  // Check authentication status on mount and when localStorage changes
  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem('authToken');
      const user = localStorage.getItem('userData');
      
      if (token && user) {
        setIsAuthenticated(true);
        setUserData(JSON.parse(user));
      } else {
        setIsAuthenticated(false);
        setUserData(null);
      }
    };

    // Check initial auth status
    checkAuthStatus();

    // Listen for storage changes (for cross-tab auth state sync)
    const handleStorageChange = (e) => {
      if (e.key === 'authToken' || e.key === 'userData') {
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleLanguage = () => {
    const languages = ['en', 'fr', 'tr'];
    const currentIndex = languages.indexOf(language);
    const nextIndex = (currentIndex + 1) % languages.length;
    setLanguage(languages[nextIndex]);
  };

  const isActiveLink = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setIsAuthenticated(false);
    setUserData(null);
    setIsOpen(false);
    navigate('/');
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img src={logo} alt="DysOrto Logo" className="logo-image" />
        </Link>

        <div className="navbar-toggle" onClick={toggleMenu}>
          <span className={`hamburger ${isOpen ? 'active' : ''}`}></span>
        </div>

        <div className={`navbar-menu ${isOpen ? 'active' : ''}`}>
          <div className="navbar-links">
            <Link 
              to="/" 
              className={`nav-link ${isActiveLink('/')}`} 
              onClick={() => setIsOpen(false)}
            >
              {t.home}
            </Link>
            {isAuthenticated ? (
              /* Post-login navigation */
              <>
                <Link 
                  to="/kids" 
                  className={`nav-link ${isActiveLink('/kids')}`} 
                  onClick={() => setIsOpen(false)}
                >
                  {t.myKids || 'My Kids'}
                </Link>
                <Link 
                  to="/my-subscription" 
                  className={`nav-link ${isActiveLink('/my-subscription')}`} 
                  onClick={() => setIsOpen(false)}
                >
                  {t.mySubscription || 'My Subscription'}
                </Link>
              </>
            ) : (
              /* Pre-login navigation */
              <>
                <Link 
                  to="/about" 
                  className={`nav-link ${isActiveLink('/about')}`} 
                  onClick={() => setIsOpen(false)}
                >
                  {t.about}
                </Link>
                <Link 
                  to="/features" 
                  className={`nav-link ${isActiveLink('/features')}`} 
                  onClick={() => setIsOpen(false)}
                >
                  {t.features}
                </Link>
                <Link 
                  to="/pricing" 
                  className={`nav-link ${isActiveLink('/pricing')}`} 
                  onClick={() => setIsOpen(false)}
                >
                  {t.pricing || 'Pricing'}
                </Link>
                <Link 
                  to="/reviews" 
                  className={`nav-link ${isActiveLink('/reviews')}`} 
                  onClick={() => setIsOpen(false)}
                >
                  {t.reviewsTitle || 'Reviews'}
                </Link>
                <Link 
                  to="/contact" 
                  className={`nav-link ${isActiveLink('/contact')}`} 
                  onClick={() => setIsOpen(false)}
                >
                  {t.contact}
                </Link>
              </>
            )}
          </div>

          <div className="navbar-auth">
            {isAuthenticated ? (
              <div className="user-menu-wrapper" ref={dropdownRef}>
                <button
                  className="user-avatar-btn"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-label="User menu"
                >
                  <svg className="user-avatar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
                  </svg>
                </button>
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <div className="user-dropdown-header">
                      {userData?.first_name || 'User'}
                    </div>
                    <Link
                      to="/profile"
                      className="user-dropdown-item"
                      onClick={() => { setUserMenuOpen(false); setIsOpen(false); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="dropdown-icon">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
                      </svg>
                      {t.profile || 'Profile'}
                    </Link>
                    <button
                      className="user-dropdown-item user-dropdown-logout"
                      onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="dropdown-icon">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      {t.logout || 'Logout'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button 
                  className="nav-link" 
                  onClick={() => {
                    setShowLoginModal(true);
                    setIsOpen(false);
                  }}
                >
                  {t.login}
                </button>
                <Link to="/register" className="nav-button" onClick={() => setIsOpen(false)}>
                  {t.register}
                </Link>
              </>
            )}
            <button className="language-toggle" onClick={toggleLanguage}>
              {language === 'en' && (
                <>
                  <svg className="flag-icon" viewBox="0 0 24 24">
                    <path fill="#ED2939" d="M4 4h16v16H4z"/>
                    <path fill="#FFFFFF" d="M4 4h10.667v16H4z"/>
                    <path fill="#002395" d="M4 4h5.333v16H4z"/>
                  </svg>
                  <span>FR</span>
                </>
              )}
              {language === 'fr' && (
                <>
                  <svg className="flag-icon" viewBox="0 0 24 24">
                    <rect width="24" height="24" fill="#E30A17"/>
                    <circle cx="10" cy="12" r="5" fill="#FFFFFF"/>
                    <circle cx="11.5" cy="12" r="4" fill="#E30A17"/>
                    <polygon fill="#FFFFFF" points="13,12 15,10.5 14,12 15,13.5" />
                  </svg>
                  <span>TR</span>
                </>
              )}
              {language === 'tr' && (
                <>
                  <svg className="flag-icon" viewBox="0 0 24 24">
                    <rect width="24" height="24" fill="#FFFFFF"/>
                    <rect width="24" height="1.85" y="0" fill="#B31942"/>
                    <rect width="24" height="1.85" y="3.7" fill="#B31942"/>
                    <rect width="24" height="1.85" y="7.4" fill="#B31942"/>
                    <rect width="24" height="1.85" y="11.1" fill="#B31942"/>
                    <rect width="24" height="1.85" y="14.8" fill="#B31942"/>
                    <rect width="24" height="1.85" y="18.5" fill="#B31942"/>
                    <rect width="24" height="1.85" y="22.2" fill="#B31942"/>
                    <rect width="10" height="12.95" fill="#002868"/>
                  </svg>
                  <span>EN</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {showLoginModal && (
        <LoginModal 
          onClose={() => setShowLoginModal(false)} 
          language={language}
        />
      )}
    </nav>
  );
}

export default Navbar;
